/**
 * React-подписки на события клиента.
 *
 * Почему так, а не через store: MatrixClient — это EventEmitter, и
 * единственный надёжный способ живого обновления — пересобирать снимок
 * состояния на каждое релевантное событие. Пересборка списка комнат
 * дешёвая (десятки-сотни комнат), поэтому не оптимизируем преждевременно,
 * но события дебаунсим, чтобы initial sync не вызвал сотню рендеров.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ClientEvent,
  RoomEvent,
  RoomStateEvent,
  type MatrixClient,
} from "matrix-js-sdk";

import { buildRoomLists, type RoomLists } from "./rooms";

const EMPTY: RoomLists = {
  invites: [],
  joined: [],
  spaces: [],
  totals: { allUnread: 0, homeUnread: 0, allHighlight: 0, unreadChats: 0 },
};

/** Живой список комнат: обновляется сам на sync и на события таймлайна. */
export function useRoomLists(client: MatrixClient | null): RoomLists {
  const [lists, setLists] = useState<RoomLists>(EMPTY);
  // Таймер дебаунса держим в ref, чтобы не пересоздавать эффект.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!client) {
      setLists(EMPTY);
      return;
    }

    const recompute = () => setLists(buildRoomLists(client));

    // 120 мс: незаметно для человека, но склеивает пачку событий из sync.
    const schedule = () => {
      if (timer.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        recompute();
      }, 120);
    };

    recompute();

    client.on(ClientEvent.Sync, schedule);
    // ЗАМЕРЕННЫЙ БАГ (Антон, 2026-09-10 11:59): свежее приглашение в комнату,
    // о которой клиент раньше не знал, НЕ появлялось в списке до перезагрузки
    // страницы. Причина в порядке внутри sync.js: для brand-new invite-комнаты
    // сначала идёт `client.emit(ClientEvent.Room)` (строка 1062), а проброс
    // событий комнаты наверх (`reEmitter.reEmit`, строка 1659) навешивается
    // при СОЗДАНИИ объекта комнаты — то есть MyMembership для такой комнаты
    // на клиента не приходит вообще, слушать его недостаточно.
    // ClientEvent.Room — единственное событие, которое гарантированно
    // сообщает «появилась новая комната», включая приглашения.
    client.on(ClientEvent.Room, schedule);
    // Симметрично: комната исчезла (forget/отклонённое приглашение) — иначе
    // в списке остаётся мёртвая строка.
    client.on(ClientEvent.DeleteRoom, schedule);
    client.on(RoomEvent.Timeline, schedule);
    client.on(RoomEvent.Name, schedule);
    client.on(RoomEvent.MyMembership, schedule);
    // RoomEvent.UnreadNotifications сознательно НЕ слушаем: в matrix-js-sdk 42.3.0
    // оно испускается только на Room, но не пробрасывается на MatrixClient
    // (нет в его EmittedEvents — проверено типами). Счётчики всё равно
    // пересчитываются на Sync/Timeline/Receipt — этого достаточно.
    client.on(RoomEvent.Receipt, schedule);
    // Тег m.favourite (закрепление) и список m.direct (личные чаты) — это
    // account data, а не события таймлайна. Без этих двух подписок вкладка
    // «Личные» и порядок закреплённых обновлялись бы только по счастливой
    // случайности — на следующем чужом событии.
    client.on(RoomEvent.Tags, schedule);
    client.on(ClientEvent.AccountData, schedule);
    // Состав пространств (m.space.child) — это СОБЫТИЯ СОСТОЯНИЯ, а не сообщения,
    // и в RoomEvent.Timeline они не приходят. Без этой подписки вкладка пространства
    // не заметила бы добавление или удаление комнаты до перезагрузки страницы.
    client.on(RoomStateEvent.Events, schedule);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      client.off(ClientEvent.Sync, schedule);
      client.off(ClientEvent.Room, schedule);
      client.off(ClientEvent.DeleteRoom, schedule);
      client.off(RoomEvent.Timeline, schedule);
      client.off(RoomEvent.Name, schedule);
      client.off(RoomEvent.MyMembership, schedule);
      client.off(RoomEvent.Receipt, schedule);
      client.off(RoomEvent.Tags, schedule);
      client.off(ClientEvent.AccountData, schedule);
      client.off(RoomStateEvent.Events, schedule);
    };
  }, [client]);

  return lists;
}

/** Состояние синхронизации — нужно, чтобы не показывать «нет комнат» во время загрузки. */
export function useSyncState(client: MatrixClient | null): string | null {
  const [state, setState] = useState<string | null>(null);

  useEffect(() => {
    if (!client) return;
    setState(client.getSyncState());
    const onSync = (s: string) => setState(s);
    client.on(ClientEvent.Sync, onSync);
    return () => {
      client.off(ClientEvent.Sync, onSync);
    };
  }, [client]);

  return state;
}

/** Комната по id — мемоизируем, чтобы не дёргать getRoom на каждый рендер. */
export function useRoom(client: MatrixClient | null, roomId: string | null) {
  /**
   * getRoom В МОМЕНТ первого рендера может честно ответить null: при входе
   * по прямой ссылке (#/room/<id>) комната доезжает только со sync, а мемо
   * по [client, roomId] этого не заметил бы и оставил null навсегда — это
   * и был бы «белый экран» по permalink'у. Поэтому подписываемся на
   * появление комнаты в сторе: RoomEvent.MyMembership (join/invite/leave
   * меняет видимость комнаты) и Sync (первый ответ сервера наполняет стор).
   * version — просто счётчик, заставляющий мемо пересчитаться.
   */
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!client) return;
    const bump = () => setVersion((v) => v + 1);
    client.on(RoomEvent.MyMembership, bump);
    client.on(ClientEvent.Sync, bump);
    return () => {
      client.off(RoomEvent.MyMembership, bump);
      client.off(ClientEvent.Sync, bump);
    };
  }, [client]);

  return useMemo(() => {
    if (!client || !roomId) return null;
    return client.getRoom(roomId);
    // version — триггер пересчёта при наполнении стора (см. выше).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, roomId, version]);
}
